import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const SplitSalesmanTargetModal = ({ show, branch, salesmen, month, year, onClose, onSuccess }) => {
    const [targets, setTargets] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && salesmen.length > 0) {
            // Initialize targets with existing values or equal split
            const initialTargets = {};
            const existingTotal = salesmen.reduce((sum, s) => sum + (s.salesmanTarget || 0), 0);
            
            if (existingTotal > 0) {
                // Use existing targets
                salesmen.forEach(s => {
                    initialTargets[s.salesmanId._id || s.salesmanId] = s.salesmanTarget || 0;
                });
            } else {
                // Equal split of branch target
                const equalShare = branch.target > 0 ? (branch.target / salesmen.length).toFixed(2) : 0;
                salesmen.forEach(s => {
                    initialTargets[s.salesmanId._id || s.salesmanId] = parseFloat(equalShare);
                });
            }
            
            setTargets(initialTargets);
        }
    }, [show, salesmen, branch]);

    const handleTargetChange = (salesmanId, value) => {
        setTargets(prev => ({
            ...prev,
            [salesmanId]: parseFloat(value) || 0
        }));
    };

    const getTotalAllocated = () => {
        return Object.values(targets).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
    };

    const handleDistributeEqually = () => {
        const newTargets = { ...targets };
        
        // Identify salesmen with manually set targets (> 0) and those without
        const salesmenWithTargets = [];
        const salesmenWithoutTargets = [];
        let allocatedAmount = 0;
        
        salesmen.forEach(s => {
            const salesmanId = s.salesmanId._id || s.salesmanId;
            const currentTarget = parseFloat(targets[salesmanId]) || 0;
            
            if (currentTarget > 0) {
                salesmenWithTargets.push(salesmanId);
                allocatedAmount += currentTarget;
            } else {
                salesmenWithoutTargets.push(salesmanId);
            }
        });
        
        // Scenario 1: No targets set - split total equally among all
        if (salesmenWithTargets.length === 0) {
            const equalShare = branch.target > 0 ? (branch.target / salesmen.length).toFixed(2) : 0;
            salesmen.forEach(s => {
                newTargets[s.salesmanId._id || s.salesmanId] = parseFloat(equalShare);
            });
        } 
        // Scenario 2: Some targets set - split remaining among those without targets
        else if (salesmenWithoutTargets.length > 0) {
            const remaining = branch.target - allocatedAmount;
            
            if (remaining > 0) {
                const sharePerPerson = (remaining / salesmenWithoutTargets.length).toFixed(2);
                salesmenWithoutTargets.forEach(salesmanId => {
                    newTargets[salesmanId] = parseFloat(sharePerPerson);
                });
            } else {
                // If remaining is 0 or negative, set others to 0
                salesmenWithoutTargets.forEach(salesmanId => {
                    newTargets[salesmanId] = 0;
                });
            }
        }
        // Scenario 3: All salesmen have targets - do nothing (or could reset all)
        
        setTargets(newTargets);
    };

    const handleDistributeProportionally = () => {
        // Distribute based on current sales performance
        const totalSales = salesmen.reduce((sum, s) => sum + (s.totalSales || 0), 0);
        if (totalSales === 0) {
            handleDistributeEqually();
            return;
        }

        const newTargets = {};
        salesmen.forEach(s => {
            const proportion = (s.totalSales || 0) / totalSales;
            newTargets[s.salesmanId._id || s.salesmanId] = parseFloat((branch.target * proportion).toFixed(2));
        });
        setTargets(newTargets);
    };

    const handleResetAll = () => {
        const newTargets = {};
        salesmen.forEach(s => {
            newTargets[s.salesmanId._id || s.salesmanId] = 0;
        });
        setTargets(newTargets);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const totalAllocated = getTotalAllocated();
            
            // Validation: total allocated should not exceed branch target significantly
            if (totalAllocated > branch.target * 1.1) {
                setError(`Total allocated (${totalAllocated.toFixed(2)}) exceeds branch target (${branch.target}) by more than 10%`);
                setLoading(false);
                return;
            }

            const salesmanTargets = Object.entries(targets).map(([salesmanId, target]) => ({
                salesmanId,
                target: parseFloat(target)
            }));

            const response = await axiosInstance.post('/api/sales-target/salesman-targets/split', {
                branchId: branch._id,
                month: parseInt(month),
                year: parseInt(year),
                salesmanTargets
            });

            if (response.data.success) {
                onSuccess();
                handleClose();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to split targets');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setTargets({});
            setError(null);
            onClose();
        }
    };

    if (!show) return null;

    const totalAllocated = getTotalAllocated();
    const remaining = branch.target - totalAllocated;
    // Use a small epsilon for floating-point comparison (allow 0.01 difference)
    const isOverAllocated = totalAllocated > (branch.target + 0.01);

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <h2>Split Target: {branch.name}</h2>
                <p className="modal-subtitle">Allocate branch target to individual salesmen for {month}/{year}</p>

                {error && <div className="error-message">{error}</div>}

                <div className="split-summary">
                    <div className="split-stat">
                        <span className="split-label">Branch Target</span>
                        <span className="split-value">{branch.target.toLocaleString()}</span>
                    </div>
                    <div className="split-stat">
                        <span className="split-label">Total Allocated</span>
                        <span className={`split-value ${totalAllocated > (branch.target + 0.01) ? 'error-text' : ''}`}>
                            {totalAllocated.toLocaleString()}
                        </span>
                    </div>
                    <div className="split-stat">
                        <span className="split-label">Remaining</span>
                        <span className={`split-value ${remaining < -0.01 ? 'error-text' : 'success-text'}`}>
                            {Math.abs(remaining) < 0.01 ? '0' : remaining.toLocaleString()}
                        </span>
                    </div>
                </div>

                {remaining > 0.01 && (
                    <div
                        style={{
                            margin: '0 0 12px', padding: '8px 12px', borderRadius: 6,
                            background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
                            fontSize: '0.85rem', lineHeight: 1.4
                        }}
                    >
                        ⚠ <strong>{remaining.toLocaleString()}</strong> of the branch target is unallocated.
                        Use <strong>Equal Split</strong> or assign it manually so the salesmen targets sum to the
                        branch target ({branch.target.toLocaleString()}) — otherwise this branch's MTD total will
                        fall short of its target.
                    </div>
                )}

                <div className="split-actions-bar">
                    <button 
                        type="button" 
                        onClick={handleDistributeEqually}
                        className="btn-secondary btn-sm"
                        disabled={loading}
                    >
                        📊 Equal Split
                    </button>
                    <button 
                        type="button" 
                        onClick={handleDistributeProportionally}
                        className="btn-secondary btn-sm"
                        disabled={loading}
                    >
                        📈 By Performance
                    </button>
                    <button 
                        type="button" 
                        onClick={handleResetAll}
                        className="btn-secondary btn-sm"
                        disabled={loading}
                        title="Reset all targets to 0"
                    >
                        🔄 Reset
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="split-table-wrapper">
                        <table className="split-table">
                            <thead>
                                <tr>
                                    <th>Salesman</th>
                                    <th className="align-right">Current Sales</th>
                                    <th className="align-right">Target</th>
                                    <th className="align-right">Achievement %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {salesmen.map((s) => {
                                    const salesmanId = s.salesmanId._id || s.salesmanId;
                                    const target = targets[salesmanId] || 0;
                                    const achievement = target > 0 ? ((s.totalSales / target) * 100).toFixed(1) : 0;
                                    
                                    return (
                                        <tr key={salesmanId}>
                                            <td>
                                                <div className="salesman-info">
                                                    <span className="salesman-name">{s.salesmanName}</span>
                                                    <span className="salesman-id">{s.employeeId}</span>
                                                </div>
                                            </td>
                                            <td className="align-right">{s.totalSales.toLocaleString()}</td>
                                            <td className="align-right">
                                                <input
                                                    type="number"
                                                    value={target}
                                                    onChange={(e) => handleTargetChange(salesmanId, e.target.value)}
                                                    min="0"
                                                    step="0.01"
                                                    className="target-input"
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td className="align-right">
                                                <span className={achievement >= 100 ? 'success-text' : ''}>
                                                    {achievement}%
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="modal-actions">
                        <button 
                            type="button" 
                            onClick={handleClose} 
                            className="btn-secondary" 
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn-primary" 
                            disabled={loading || isOverAllocated}
                        >
                            {loading ? 'Saving...' : 'Save Targets'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SplitSalesmanTargetModal;
